import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadRfpPdf, analyzeRfpById, searchSuppliers } from '../../store/rfpSlice';
import { HiOutlineDocumentText, HiCheckCircle, HiX } from 'react-icons/hi';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import Vector from '../../assets/LandingPage/Vector.png';
import { FaPlay } from 'react-icons/fa6';
import CompanyAvatar from '../UILibrary/CompanyAvatar';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

function SupplierCard({
  companyName,
  industry,
  location,
  rating,
  certified,
  matchScore,
  description,
}) {
  return (
    <div className="relative bg-white border border-[#E6E6E6] rounded-2xl p-4 text-center shadow-sm hover:shadow-md transition">
      <div className="absolute top-4 right-4 bg-[#FFE600] text-black text-sm font-semibold px-3 py-1 rounded-lg">
        {matchScore}%
      </div>

      <div className="flex justify-center mb-4">
        <CompanyAvatar companyName={companyName} size={54} />
      </div>


      <h4 className="text-lg font-bold text-[#0A0A0A] mb-1">{companyName}</h4>

      <p className="text-sm text-[#6B7280]">{industry}</p>
      <p className="text-sm text-[#6B7280] mb-4">{location}</p>


      <div className="flex items-center justify-center mb-6">
        {/* <p className="mr-2 text-gray-500">Rating:</p>
        <p className="ml-2 bg-[#EAF524] rounded px-3 py-1 text-sm whitespace-nowrap">
          {'No rating yet'}
        </p> */}

        {certified && (
          <span className="flex items-center gap-1 text-sm text-[#0A66FF] font-medium">
            Certified
            <HiCheckCircle className="w-4 h-4" />
          </span>
        )}
      </div>


      <div className="text-left">
        <h5 className="text-sm font-semibold text-[#111827] mb-1">About</h5>

        <p className="text-sm text-[#6B7280] leading-relaxed line-clamp-4">{description}</p>
      </div>
    </div>
  );
}



export default function RfpMatchingSection() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const { loading } = useSelector((state) => state.rfp);
  const [isGenerating, setIsGenerating] = useState(false);
  const isMobile = window.innerWidth < 768;


  const sectionRef = useRef(null);
  const elementsRef = useRef([]);

  const addToRefs = (el) => {
    if (el && !elementsRef.current.includes(el)) {
      elementsRef.current.push(el);
    }
  };

useEffect(() => {

  const fromNavbar = sessionStorage.getItem('animateRfpSection') === 'true';
  if (!fromNavbar) return;

  // Clear flag so it doesn't re-run on back/refresh
  sessionStorage.removeItem('animateRfpSection');

  // Clear stale refs (important on navigation)
  elementsRef.current = [];

  // Skip animations on mobile for speed
  if (window.innerWidth < 768) {
    gsap.set(elementsRef.current, { opacity: 1, y: 0 });
    return;
  }

  const ctx = gsap.context(() => {
    gsap.from(elementsRef.current, {
      opacity: 0,
      y: 16,
      duration: 0.4,
      ease: 'power2.out',
      stagger: 0.08,
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top 95%',
        once: true,
      },
    });


    gsap.fromTo(
      '.cta-btn',
      { scale: 1 },
      {
        scale: 1.04,
        duration: 1.2,
        ease: 'power1.inOut',
        repeat: -1,
        yoyo: true,
      }
    );
  }, sectionRef);

  return () => ctx.revert();
}, []);



  const handleUpload = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setFileName(selected.name);
    }
  };

  const clearSearch = () => {
    setSuppliers([]);
    setFile(null);
    setFileName('');
    setIsGenerating(false);
  };

  function mapSupplier(apiItem) {
    const address = apiItem.address?.type?.[0];

    return {
      companyName: apiItem.company?.name,
      industry: apiItem.industry?.name,
      location: address
        ? `${address.city || ''}, ${address.state || ''}, ${address.country || ''}`
        : 'Location unavailable',
      rating: apiItem.rating || 'No rating yet',
      matchScore: Math.round(apiItem.similarity * 100),
      certified: apiItem.diversity?.certificationType?.length > 0,
      description: apiItem.company?.description || 'No description available for this company.',
    };
  }

  const handleGenerate = async () => {
    if (!file || isGenerating) return;

    try {
      setIsGenerating(true);

      const uploadResult = await dispatch(uploadRfpPdf(file));
      const fileId = uploadResult?.payload?.fileId;
      if (!fileId) return;

      const analysisResult = await dispatch(analyzeRfpById(fileId));
      const summaryText = analysisResult?.payload?.analyzeSummary;
      if (!summaryText) return;

      const supplierResult = await dispatch(
        searchSuppliers({ text: summaryText, page: 1, pageSize: 4 }),
      );

      const mapped = (supplierResult?.payload || []).slice(0, 4).map(mapSupplier);

      setSuppliers(mapped);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <section
    ref={sectionRef}
      className="relative bg-cover bg-center py-20 px-6"
      style={
        isMobile
          ? {
              backgroundImage: `url(${Vector})`,
              backgroundRepeat: 'repeat',
              backgroundSize: 'contain',
            }
          : {}
      }
    >

      {!isMobile && (
        <img
          src={Vector}
          alt="Background"
          className="absolute inset-0 h-full w-full object-cover z-0 pointer-events-none"
          style={{ zIndex: 0 }}
        />
      )}

      <div ref={addToRefs} className="relative max-w-7xl mx-auto text-center z-10 mt-10">
        {/* Badge */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#EAF3FF] text-[#0A66FF] px-4 py-1.5 rounded-full text-sm font-medium">
            <HiOutlineDocumentText className="w-4 h-4" />
            AI RFP Matching
          </div>
        </div>


        <h2 ref={addToRefs} className="text-4xl md:text-5xl font-bold mb-4">Try AI RFP Matching in Seconds</h2>

        <p ref={addToRefs} className="text-lg text-[#6B7280] max-w-3xl mx-auto mb-10">
          Upload a sample RFP or project brief below to see our AI instantly identify your top
          supplier matches.
        </p>


        <div
        ref={addToRefs}
          className="
    mx-auto max-w-3xl
    rounded-2xl border border-[#E6E6E6] bg-white
    px-4 py-5
    sm:px-6 sm:py-4
    flex flex-col gap-4
    lg:flex-row lg:items-center lg:justify-between lg:gap-6
  "
        >

          <div className="flex items-start gap-3">
            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#F3F4F6]">
              <HiOutlineDocumentText className="h-5 w-5 text-[#6B7280]" />
            </div>

            <div className="text-left">

              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-medium text-[#0A0A0A] break-all sm:break-normal">
                  {fileName || 'Company RFP document'}
                </p>
                {file && <HiCheckCircle className="h-4 w-4 text-green-600" />}
              </div>

              <p className="mb-2 text-xs text-[#6B7280]">PDF</p>

              <label
                className={`
          inline-flex items-center gap-2
          rounded-lg border border-[#E6E6E6]
          bg-[#F9FAFB]
          px-3 py-1.5
          text-sm font-medium text-[#6B7280]
          transition hover:bg-[#F3F4F6]
          cursor-pointer
          ${loading ? 'pointer-events-none opacity-50' : ''}
        `}
              >
                <HiOutlineDocumentText className="h-4 w-4" />
                Upload new document
                <input
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleUpload}
                  disabled={loading}
                />
              </label>
            </div>
          </div>


          <button
            onClick={handleGenerate}
            disabled={!file || isGenerating}
            className={`
      flex items-center justify-center gap-2
      rounded-lg px-6 py-3 font-semibold
      w-full lg:w-auto
      ${
        !file || isGenerating
          ? 'bg-[#FFE600]/50 cursor-not-allowed'
          : 'bg-[#FFE600] hover:opacity-90'
      }
    `}
          >
            {isGenerating && <AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" />}
            {isGenerating ? 'Generating…' : 'Generate'}
          </button>
        </div>


<div
  className={`
    mt-16 overflow-hidden transition-[max-height,opacity] duration-500 ease-out
    ${suppliers.length
      ? 'max-h-[2000px] md:max-h-[520px] opacity-100'
      : 'max-h-0 opacity-0'}
  `}
>

          {suppliers.length > 0 && (
            <>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl">Top recommended suppliers</h3>
                <button onClick={clearSearch} className="text-[#7B7B7B] text-sm">
                  ✕ Clear search
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {suppliers.map((supplier, index) => (
                  <SupplierCard key={index} {...supplier} />
                ))}
              </div>
            </>
          )}
        </div>

        <button
          onClick={() => navigate('/signup')}
          className="mt-14 bg-[#0A66FF] text-white px-10 py-3 rounded-xl font-semibold hover:opacity-90 inline-flex items-center gap-3 cursor-pointer"
        >
          Unlock Full Supplier Matches
          <FaPlay className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}